import React from 'react';
import { SearchIcon, BellIcon, MailIcon, UserIcon, LayoutGridIcon, PawPrintIcon, FileTextIcon, CreditCardIcon, AlertTriangleIcon, BarChart3Icon, ClockIcon, CheckIcon } from 'lucide-react';
import { ctaDashboard, type Bi } from '../../data/animalsFinalCta';
type P = {
  isFR: boolean;
};
const pk = (isFR: boolean) => (v: Bi) => isFR ? v.fr : v.en;
// Exact logo files used in the site header (components/Header.tsx)
const BCITI_LOGO = 'https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png';
const ATNA_LOGO = 'https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png';
const NAV_ICONS = {
  overview: LayoutGridIcon,
  animals: PawPrintIcon,
  licences: FileTextIcon,
  payments: CreditCardIcon,
  reminders: BellIcon,
  reports: AlertTriangleIcon,
  analytics: BarChart3Icon
} as const;
const PANEL = 'rounded-xl border border-[#EEEEF4] bg-white shadow-[0_10px_24px_-22px_rgba(40,36,120,0.45)]';
function KpiIcon({
  k
}: {
  k: 'registered' | 'renew' | 'expired';
}) {
  if (k === 'registered') return <span className="w-10 h-10 rounded-full border-[4px] border-[#1F9D7A] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-156" data-mp="jhjpz5skm5ya">
        <CheckIcon className="w-3.5 h-3.5 text-[#1F9D7A]" strokeWidth={3} data-id="element-157" data-mp="xidw1hs5buts" />
      </span>;
  if (k === 'renew') return <span className="w-10 h-10 rounded-full bg-[#FEF1E6] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-158" data-mp="sgwztzklwwjj">
        <ClockIcon className="w-5 h-5 text-[#EA7A2C]" strokeWidth={1.8} data-id="element-159" data-mp="12gfh2894u47w" />
      </span>;
  return <span className="w-10 h-10 rounded-full bg-[#FDECEC] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-160" data-mp="1g7sy9fjkipzr">
      <AlertTriangleIcon className="w-5 h-5 text-[#E5484D]" strokeWidth={1.8} data-id="element-161" data-mp="1c5papsjmjuy4" />
    </span>;
}
function TrendChart({
  isFR
}: P) {
  const w = 220;
  const h = 60;
  const pts = ctaDashboard.points.map((v, i) => [8 + i * (w - 16) / (ctaDashboard.points.length - 1), h - v / 70 * h] as const);
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x},${y}`).join(' ');
  const area = `${line} L${pts[pts.length - 1][0]},${h} L${pts[0][0]},${h} Z`;
  const months = isFR ? ctaDashboard.months.fr : ctaDashboard.months.en;
  return <>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[60px]" aria-hidden="true" data-id="element-162" data-mp="rsmh0vt09cwd">
        <path d={area} fill="#EEEDFD" data-id="element-163" data-mp="wlnv51le18ew" />
        <path d={line} fill="none" stroke="#4F46E5" strokeWidth="1.8" data-id="element-164" data-mp="wmqodg66nmxz" />
        {pts.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="2.6" fill="#4F46E5" data-id="element-165" data-mp="16j1z41q4k4qb" data-mpi={i} />)}
      </svg>
      <div className="flex justify-between px-1 mt-1 font-sans text-[9.5px] text-[#8A90A2]" data-id="element-166" data-mp="16kwnjq0luez5">
        {months.map(m => <span key={m} data-id="element-167" data-mp="1fji1f7v4t5tl">{m}</span>)}
      </div>
    </>;
}
function StatusDonut({
  isFR
}: P) {
  const pick = pk(isFR);
  const r = 36;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return <div className="flex items-center gap-4" data-id="element-168" data-mp="3haldm1ko6sg9">
      <svg viewBox="0 0 100 100" className="w-[86px] h-[86px] -rotate-90 shrink-0" aria-hidden="true" data-id="element-169" data-mp="3hbmysyvegvbp">
        {ctaDashboard.split.map(s => {
        const len = s.pct / 100 * circ;
        const el = <circle key={s.key} cx="50" cy="50" r={r} fill="none" stroke={s.color} strokeWidth="12" strokeDasharray={`${len} ${circ - len}`} strokeDashoffset={-offset} data-id="element-170" data-mp="34zknepit82e9" />;
        offset += len;
        return el;
      })}
      </svg>
      <ul className="space-y-1.5" data-id="element-171" data-mp="3sqg9d60l43dl">
        {ctaDashboard.split.map(s => <li key={s.key} className="flex items-center gap-2 font-sans text-[11px] text-[#3A4258] whitespace-nowrap" data-id="element-172" data-mp="3sodlosmwmmc1">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{
          background: s.color
        }} aria-hidden="true" data-id="element-173" data-mp="3isa8mrqil3ab" />
            <span className="font-semibold" data-id="element-174" data-mp="3er87u6gy8j68">{s.pct}{isFR ? ' %' : '%'}</span>
            <span data-id="element-175" data-mp="27d55xnqdv4qp">{pick(s.label)}</span>
          </li>)}
      </ul>
    </div>;
}
export function AnimalsCtaLaptop({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div data-id="element-176" data-mp="23a8jflxq2ohh">
      <div className="rounded-t-[22px] bg-[#1B1D26] p-[9px] pb-[12px]" data-id="element-177" data-mp="23dmm6vaaszpg">
        <div className="rounded-[14px] bg-[#FCFCFE] overflow-hidden" data-id="element-178" data-mp="3r6n344xssb2w">
          {/* Top bar */}
          <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-[#F0F0F6]" data-id="element-179" data-mp="3vzpwabynxiik">
            <img src={BCITI_LOGO} alt="BCITI" className="h-6 w-auto object-contain" data-id="element-180" data-mp="3hbcxz8c3rnkw" />
            <span className="text-[#121212]/25 font-sans text-sm font-light select-none" aria-hidden="true" data-id="element-181" data-mp="3ma6e78irxsor">|</span>
            <img src={ATNA_LOGO} alt="ATNA" className="h-5 w-auto object-contain" data-id="element-182" data-mp="dtuu1bzgb0pr" />
            <span className="ml-auto flex items-center gap-3 text-[#8A90A2]" aria-hidden="true" data-id="element-183" data-mp="ird63b8rlw1z">
              <SearchIcon className="w-3.5 h-3.5" data-id="element-184" data-mp="4rllusrnnjfj" />
              <MailIcon className="w-3.5 h-3.5" data-id="element-185" data-mp="4uzqk7nsk4q5" />
              <BellIcon className="w-3.5 h-3.5" data-id="element-186" data-mp="8t712krnw9sw" />
              <span className="w-7 h-7 rounded-full bg-[#4F46E5] flex items-center justify-center" data-id="element-187" data-mp="2pivsinnaga2e">
                <UserIcon className="w-3.5 h-3.5 text-white" data-id="element-188" data-mp="2tkhcesy8pom3" />
              </span>
            </span>
          </div>
          <div className="flex" data-id="element-189" data-mp="2m0ontqrgxfmp">
            <ul className="hidden sm:block w-[140px] shrink-0 bg-white border-r border-[#F2F2F7] p-2.5 space-y-1" data-id="element-190" data-mp="2lzn6x5nrtyx2">
              {ctaDashboard.nav.map(n => {
              const Icon = NAV_ICONS[n.key];
              const active = n.key === 'overview';
              return <li key={n.key} className={`flex items-center gap-2 rounded-lg px-2.5 py-2 font-sans text-[11.5px] whitespace-nowrap ${active ? 'bg-[#EEEDFD] text-[#4F46E5] font-semibold' : 'text-[#3A4258]'}`} data-id="element-191" data-mp="2r12tx39iefi3">
                    <Icon className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-[#4F46E5]' : 'text-[#8A90A2]'}`} strokeWidth={1.8} aria-hidden="true" data-id="element-192" data-mp="2d1tdq3lazle4" />
                    {pick(n.label)}
                  </li>;
            })}
            </ul>
            <div className="flex-1 min-w-0 p-4" data-id="element-193" data-mp="1fl4d2d83ap6b">
              <p className="font-sans text-[18px] font-semibold text-[#121A3A]" data-id="element-194" data-mp="1fkct5znuufvv">{pick(ctaDashboard.title)}</p>
              <ul className="mt-3 grid grid-cols-3 gap-2.5" data-id="element-195" data-mp="258rsu44cx97f">
                {ctaDashboard.kpis.map(k => <li key={k.key} className={`${PANEL} p-3 flex items-center gap-2.5`} data-id="element-196" data-mp="2988wqt44xyah">
                    <KpiIcon k={k.key} data-id="element-197" data-mp="2e3et6lv5yvct" />
                    <span className="min-w-0" data-id="element-198" data-mp="2j0y69ig4l236">
                      <span className="block font-sans text-[18px] font-semibold text-[#121A3A] leading-none" data-id="element-199" data-mp="2izn1pgeuvqb0">{pick(k.value)}</span>
                      <span className="block font-sans text-[10.5px] text-[#5B6478] mt-1 leading-tight" data-id="element-200" data-mp="1qbr9op08emkk">{pick(k.label)}</span>
                      {k.note && <span className="block font-sans text-[9px] text-[#8A90A2] leading-tight" data-id="element-201" data-mp="1v9sqf3a6q9sv">{pick(k.note)}</span>}
                    </span>
                  </li>)}
              </ul>
              <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-[1fr_1.1fr] gap-2.5" data-id="element-202" data-mp="36oxxejhcmuia">
                <div className={`${PANEL} p-3`} data-id="element-203" data-mp="3boastsmad9wh">
                  <p className="font-sans text-[11.5px] font-semibold text-[#121A3A] mb-2" data-id="element-204" data-mp="3br6ofaanvmhr">{pick(ctaDashboard.trendTitle)}</p>
                  <TrendChart isFR={isFR} data-id="element-205" data-mp="v2xfs8fxbik4" />
                </div>
                <div className={`${PANEL} p-3`} data-id="element-206" data-mp="v4qnlr29w8tp">
                  <p className="font-sans text-[11.5px] font-semibold text-[#121A3A] mb-2" data-id="element-207" data-mp="qb5mhq5duyqa">{pick(ctaDashboard.splitTitle)}</p>
                  <StatusDonut isFR={isFR} data-id="element-208" data-mp="lc2o2ojwrqra" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Laptop base */}
      <div className="h-[14px] mx-[-3%] rounded-b-[14px] bg-[#C9CBD3] shadow-[0_24px_40px_-24px_rgba(20,20,60,0.6)]" aria-hidden="true" data-id="element-209" data-mp="cbmq1ulxpbtt" />
    </div>;
}